'use client';

import { useEffect, useState } from 'react';

const DEFAULT_FALLBACK_MS = 4000;
const IDLE_TIMEOUT_MS = 2000;

/**
 * Returns true after LCP (then an idle callback), or after a fallback timeout.
 * Use to defer non-critical third-party scripts off the critical network path.
 */
export const useLoadAfterLcp = (fallbackMs = DEFAULT_FALLBACK_MS): boolean => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let enabled = false;
    let idleId: number | undefined;
    let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
    let idleFallbackTimer: ReturnType<typeof setTimeout> | undefined;
    let observer: PerformanceObserver | undefined;

    const enable = () => {
      if (cancelled || enabled) {
        return;
      }
      enabled = true;
      if (fallbackTimer !== undefined) {
        clearTimeout(fallbackTimer);
      }
      setReady(true);
    };

    const scheduleIdle = () => {
      if (cancelled || enabled) {
        return;
      }
      if (typeof globalThis.requestIdleCallback === 'function') {
        idleId = globalThis.requestIdleCallback(() => enable(), { timeout: IDLE_TIMEOUT_MS });
      } else {
        idleFallbackTimer = setTimeout(enable, 0);
      }
    };

    try {
      observer = new PerformanceObserver((list) => {
        if (list.getEntries().length > 0) {
          observer?.disconnect();
          scheduleIdle();
        }
      });
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch {
      scheduleIdle();
    }

    fallbackTimer = setTimeout(() => {
      observer?.disconnect();
      enable();
    }, fallbackMs);

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (idleId !== undefined) {
        globalThis.cancelIdleCallback?.(idleId);
      }
      if (fallbackTimer !== undefined) {
        clearTimeout(fallbackTimer);
      }
      if (idleFallbackTimer !== undefined) {
        clearTimeout(idleFallbackTimer);
      }
    };
  }, [fallbackMs]);

  return ready;
};
